'use client';

import type { FC } from 'react';
import {
  Alert,
  Badge,
  Button,
  Card,
  Flex,
  Input,
  Modal,
  Pagination,
  Segmented,
  Select,
  Space,
  Tag,
  Typography,
} from 'antd';
import { EvimoPagination } from '~/app/components/common/EvimoTable/EvimoPagination';
import {
  BookOutlined,
  FileSearchOutlined,
  GlobalOutlined,
  PlusOutlined,
  SearchOutlined,
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { KmsUploadPanel } from '~/app/components/kms/KmsUploadPanel/KmsUploadPanel';
import { KmsHitlQueue } from '~/app/components/kms/KmsHitlQueue/KmsHitlQueue';
import type { KmsLinkedDocumentItem } from '~/app/services/kms/kms-document.types';
import { useKmsLibraryColumns } from './hooks/useKmsLibraryColumns';
import { useKmsLibraryFilterOptions } from './hooks/useKmsLibraryFilterOptions';
import type { KmsBrowseTarget, KmsLibraryMode } from './types';
import { EvimoTable } from '~/app/components/common/EvimoTable/EvimoTable';
import { KmsPageHeader } from './components/KmsPageHeader/KmsPageHeader';

const { Title, Text } = Typography;

export interface KmsPageViewProps {
  activeTab: string;
  onTabChange: (key: string) => void;
  hitlBadgeCount: number;
  isSuperAdmin: boolean;
  isPlatformLibrary: boolean;
  libraryMode: KmsLibraryMode;
  onLibraryModeChange: (mode: KmsLibraryMode) => void;
  organizationId: string;
  effectiveOrganizationId: string | null;
  selectedOrganizationName?: string | null;
  organizationOptions: { value: string; label: string }[];
  onOrganizationIdChange: (organizationId: string) => void;
  browseTarget: KmsBrowseTarget;
  onBrowseTargetChange: (target: KmsBrowseTarget) => void;
  selectedAsset: {
    id: string;
    assetName?: string | null;
    assetType?: { name?: string | null } | null;
    assetModel?: { name?: string | null } | null;
  } | null;
  linkedDocuments: KmsLinkedDocumentItem[];
  linkedDocumentsTotal: number;
  linkedDocumentsPage: number;
  linkedDocumentsPageSize: number;
  linkedDocumentsPageSizeOptions: number[];
  onLinkedDocumentsPageChange: (page: number, pageSize?: number) => void;
  documentSearch: string;
  onDocumentSearchChange: (value: string) => void;
  documentTypeFilter?: string;
  onDocumentTypeFilterChange: (value: string | undefined) => void;
  statusFilter?: string;
  onStatusFilterChange: (value: string | undefined) => void;
  isLoadingLinkedDocuments: boolean;
  linkedDocumentsError?: string | null;
  assets: {
    id: string;
    assetName?: string | null;
    assetType?: { name?: string | null } | null;
    assetModel?: { name?: string | null } | null;
  }[];
  totalAssets: number;
  isLoadingAssets: boolean;
  assetPage: number;
  onAssetPageChange: (page: number) => void;
  assetSearch: string;
  onAssetSearchChange: (value: string) => void;
  isUploadModalOpen: boolean;
  openUploadModal: () => void;
  closeUploadModal: () => void;
}

export const KmsPageView: FC<KmsPageViewProps> = ({
  activeTab,
  onTabChange,
  hitlBadgeCount,
  isSuperAdmin,
  isPlatformLibrary,
  libraryMode,
  onLibraryModeChange,
  organizationId,
  effectiveOrganizationId,
  selectedOrganizationName,
  organizationOptions,
  onOrganizationIdChange,
  browseTarget,
  onBrowseTargetChange,
  selectedAsset,
  linkedDocuments,
  linkedDocumentsTotal,
  linkedDocumentsPage,
  linkedDocumentsPageSize,
  linkedDocumentsPageSizeOptions: _linkedDocumentsPageSizeOptions,
  onLinkedDocumentsPageChange,
  documentSearch,
  onDocumentSearchChange,
  documentTypeFilter,
  onDocumentTypeFilterChange,
  statusFilter,
  onStatusFilterChange,
  isLoadingLinkedDocuments,
  linkedDocumentsError,
  assets,
  totalAssets,
  isLoadingAssets,
  assetPage,
  onAssetPageChange,
  assetSearch,
  onAssetSearchChange,
  isUploadModalOpen,
  openUploadModal,
  closeUploadModal,
}) => {
  const { t } = useTranslation('kms');
  const isHitl = activeTab === 'hitl';
  const { documentTypeOptions, statusOptions } = useKmsLibraryFilterOptions();
  const libraryColumns = useKmsLibraryColumns({
    organizationId: effectiveOrganizationId,
    isSuperAdmin,
  });
  const contentTitle = isPlatformLibrary
    ? t('library.platformTitle')
    : browseTarget === 'org-global'
      ? t('library.orgWideTitle')
      : (selectedAsset?.assetName ?? t('library.assetDocuments'));
  const contentSubtitle = isPlatformLibrary
    ? t('library.platformSubtitle')
    : browseTarget === 'org-global'
      ? t('library.orgWideSubtitle')
      : t('library.assetSubtitle');

  return (
    <div
      className="app-page app-page--kms"
      style={{ width: '100%', minWidth: 0 }}
    >
      <KmsPageHeader
        title={t('pageTitle')}
        subtitle={t('pageSubtitle')}
        icon={<BookOutlined />}
        actions={
          <Segmented
            value={isHitl ? 'hitl' : 'library'}
            onChange={(value) => onTabChange(String(value))}
            options={[
              {
                label: (
                  <Flex align="center" gap={6}>
                    <BookOutlined />
                    <span>{t('tabs.library')}</span>
                  </Flex>
                ),
                value: 'library',
              },
              {
                label: (
                  <Flex align="center" gap={6}>
                    <FileSearchOutlined />
                    <span>{t('tabs.hitl')}</span>
                    {hitlBadgeCount > 0 ? (
                      <Badge count={hitlBadgeCount} size="small" />
                    ) : null}
                  </Flex>
                ),
                value: 'hitl',
              },
            ]}
          />
        }
      />

      <Card className="kms-section-card">
        <Flex vertical gap="middle" style={{ width: '100%', minWidth: 0 }}>
          {!isHitl ? (
            <Flex gap="small" wrap="wrap" align="center">
              {isSuperAdmin ? (
                <Select
                  style={{ minWidth: 180 }}
                  value={libraryMode}
                  onChange={onLibraryModeChange}
                  options={[
                    { value: 'platform', label: t('library.platformOption') },
                    {
                      value: 'organization',
                      label: t('library.organizationOption'),
                    },
                  ]}
                />
              ) : null}

              {isSuperAdmin && libraryMode === 'organization' ? (
                <Select
                  style={{ minWidth: 220, flex: '1 1 200px', maxWidth: 360 }}
                  showSearch
                  allowClear={false}
                  optionFilterProp="label"
                  filterOption={(input, option) =>
                    String(option?.label ?? '')
                      .toLowerCase()
                      .includes(input.trim().toLowerCase())
                  }
                  placeholder={t('selectOrganization')}
                  value={organizationId || undefined}
                  onChange={onOrganizationIdChange}
                  options={organizationOptions}
                  notFoundContent={t('library.noOrganizations')}
                />
              ) : null}

              {!isPlatformLibrary ? (
                <>
                  <Button
                    type={browseTarget === 'org-global' ? 'primary' : 'default'}
                    icon={<GlobalOutlined />}
                    onClick={() => onBrowseTargetChange('org-global')}
                  >
                    {t('library.orgWideTitle')}
                  </Button>
                  <Select
                    style={{ minWidth: 220, flex: '1 1 220px', maxWidth: 400 }}
                    showSearch
                    searchValue={assetSearch}
                    placeholder={t('library.searchAssets')}
                    loading={isLoadingAssets}
                    value={
                      browseTarget === 'org-global' ? undefined : browseTarget
                    }
                    onChange={(assetId) => onBrowseTargetChange(assetId)}
                    onSearch={onAssetSearchChange}
                    filterOption={false}
                    options={assets.map((asset) => ({
                      value: asset.id,
                      label: asset.assetName ?? asset.id,
                    }))}
                    notFoundContent={t('library.noAssets')}
                    popupRender={(menu) => (
                      <>
                        {menu}
                        {totalAssets > 10 ? (
                          <div
                            style={{
                              padding: 8,
                              borderTop: '1px solid #f0f0f0',
                            }}
                          >
                            <Pagination
                              simple
                              current={assetPage}
                              total={totalAssets}
                              pageSize={10}
                              onChange={onAssetPageChange}
                            />
                          </div>
                        ) : null}
                      </>
                    )}
                  />
                </>
              ) : (
                <Tag color="purple" style={{ margin: 0 }}>
                  {t('library.platformHint')}
                </Tag>
              )}
            </Flex>
          ) : null}

          {isHitl ? (
            <KmsHitlQueue
              organizationId={effectiveOrganizationId}
              isSuperAdmin={isSuperAdmin}
              isPlatformLibrary={isPlatformLibrary}
            />
          ) : (
            <>
              <Flex
                justify="space-between"
                align="flex-start"
                gap="middle"
                wrap="wrap"
              >
                <Space direction="vertical" size={2} style={{ minWidth: 0 }}>
                  <Title
                    level={5}
                    className="ds-section-header"
                    style={{ margin: 0 }}
                  >
                    {contentTitle}
                  </Title>
                  <Text
                    type="secondary"
                    className="om-dashboard-header__subtitle"
                  >
                    {contentSubtitle}
                  </Text>
                  {!isPlatformLibrary && selectedOrganizationName ? (
                    <Text type="secondary">
                      {t('library.showingForOrg', {
                        org: selectedOrganizationName,
                        count: linkedDocumentsTotal,
                      })}
                    </Text>
                  ) : null}
                </Space>
              </Flex>

              {linkedDocumentsError ? (
                <Alert
                  type="error"
                  showIcon
                  message={t('library.loadError')}
                  description={linkedDocumentsError}
                />
              ) : null}

              {linkedDocuments.length === 0 &&
              hitlBadgeCount > 0 &&
              !isLoadingLinkedDocuments &&
              !linkedDocumentsError ? (
                <Alert
                  type="info"
                  showIcon
                  message={t('library.pendingReviewHint', {
                    count: hitlBadgeCount,
                  })}
                />
              ) : null}

              {!isPlatformLibrary &&
              !organizationId &&
              isSuperAdmin &&
              !isLoadingLinkedDocuments ? (
                <Alert
                  type="warning"
                  showIcon
                  message={t('selectOrganization')}
                  description={t('library.selectOrgToSeeDocs')}
                />
              ) : null}

              <Flex gap="middle" wrap="wrap" align="center">
                <Input.Search
                  allowClear
                  enterButton
                  prefix={<SearchOutlined />}
                  placeholder={t('library.searchDocuments')}
                  value={documentSearch}
                  onChange={(event) =>
                    onDocumentSearchChange(event.target.value)
                  }
                  onSearch={(value) => onDocumentSearchChange(value)}
                  style={{ flex: '1 1 280px', minWidth: 220, maxWidth: 480 }}
                />
                <Select
                  allowClear
                  showSearch
                  optionFilterProp="label"
                  placeholder={t('library.filterDocumentTypePlaceholder')}
                  value={documentTypeFilter}
                  onChange={(value) => onDocumentTypeFilterChange(value)}
                  options={documentTypeOptions}
                  style={{ minWidth: 200, flex: '0 1 240px' }}
                />
                <Select
                  allowClear
                  placeholder={t('library.filterStatusPlaceholder')}
                  value={statusFilter}
                  onChange={(value) => onStatusFilterChange(value)}
                  options={statusOptions}
                  style={{ minWidth: 180, flex: '0 1 220px' }}
                />
                <Button
                  type="primary"
                  icon={<PlusOutlined />}
                  onClick={openUploadModal}
                  disabled={!isPlatformLibrary && !organizationId}
                >
                  {t('uploadNewDocument')}
                </Button>
              </Flex>

              <EvimoTable
                rowKey="documentId"
                loading={isLoadingLinkedDocuments}
                dataSource={linkedDocuments}
                locale={{ emptyText: t('library.noDocuments') }}
                scroll={{ x: 1300 }}
                style={{ width: '100%' }}
                tableLayout="fixed"
                pagination={false}
                columns={libraryColumns}
              />

              <Flex justify="flex-end" style={{ width: '100%' }}>
                <EvimoPagination
                  current={linkedDocumentsPage}
                  total={linkedDocumentsTotal}
                  pageSize={linkedDocumentsPageSize}
                  onChange={onLinkedDocumentsPageChange}
                  onShowSizeChange={onLinkedDocumentsPageChange}
                />
              </Flex>
            </>
          )}
        </Flex>
      </Card>

      <Modal
        title={t('uploadNewDocument')}
        open={isUploadModalOpen}
        onCancel={closeUploadModal}
        footer={null}
        width={900}
        destroyOnClose
      >
        <KmsUploadPanel
          embedded
          organizationId={effectiveOrganizationId}
          isSuperAdmin={isSuperAdmin}
          isPlatformLibrary={isPlatformLibrary}
          presetAssetId={
            browseTarget === 'org-global' ? undefined : browseTarget
          }
        />
      </Modal>
    </div>
  );
};
